/**
 * qty-min-rules.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Migrated from Studio v4 — enforces the minimum order quantity on the product
 * page so customers cannot submit a quantity below the allowed minimum.
 *
 * The minimum itself is decided server-side in main-product.liquid (native
 * quantity_rule.min, raised to 10 for Wall Panel / Ceiling Panel collections)
 * and rendered onto the quantity input as the `min` attribute. This script
 * guarantees that value is honoured for every customer-facing path:
 *
 *   • typing / stepper      → clamp on `change`
 *   • Add to Cart           → clamp on the form `submit` (capture phase, so it
 *                             runs before product-form.js reads FormData)
 *   • Buy Now / dynamic
 *     checkout              → clamp on `pointerdown`/`mousedown` of the payment
 *                             button (capture phase, before Shopify reads the
 *                             form). The quantity input lives in a different
 *                             block than the <form>, so we clamp every product
 *                             quantity input on the page rather than relying on
 *                             DOM containment.
 *
 * Also covers the cart drawer / cart page quantity inputs (name="updates[...]"),
 * whose `min`/`data-min` are set in snippets/cart-drawer.liquid and
 * snippets/quantity-input.liquid from the same `calculator-wall-panel` tag
 * check — this script is a client-side safety net on top of the AJAX-level
 * clamp already done in cart.js's CartItems.validateQuantity.
 *
 * Why delegation: Studio v15 re-renders the product form on variant change, so
 * element-bound listeners would be lost. Delegated document listeners survive
 * any re-render and read the floor from the input's own `min` attribute, so this
 * stays correct for every product (min 1 for normal products, 10 for panels)
 * with no hardcoded IDs.
 *
 * LIMITATION (by design — read this): theme JavaScript can only protect the UI.
 * A determined user calling /cart/add.js (or the Storefront/AJAX API) directly
 * bypasses the storefront entirely and cannot be blocked from the theme. For a
 * hard, server-side guarantee, enable Shopify's native Quantity Rules on these
 * products, or add a Cart/Checkout Validation Shopify Function. This script
 * covers normal usage AND manual value tampering through the rendered controls.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(() => {
  'use strict';

  // Product-page quantity input uses name="quantity"; cart / cart-drawer line
  // inputs use name="updates[...]". Both use the same "quantity__input" class.
  const SELECTOR = 'input.quantity__input[name="quantity"], input.quantity__input[name^="updates["]';
  function clamp(input) {
    if (!input) return;
    const min = parseInt(input.getAttribute('min'), 10);
    if (!min || min <= 1) return; // nothing to enforce for normal products

    const value = parseInt(input.value, 10);
    if (isNaN(value) || value < min) {
      input.value = min;
    }
  }

  function clampAll() {
    document.querySelectorAll(SELECTOR).forEach(clamp);
  }

  // 1. Typing / stepper — clamp as soon as the field changes.
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (t && t.matches && t.matches(SELECTOR)) clamp(t);
    },
    true
  );

  // Studio 15 quantity buttons support
document.addEventListener(
  'click',
  () => {
    setTimeout(() => {
      document.querySelectorAll(SELECTOR).forEach(clamp);
    }, 0);
  },
  true
);

  // 2. Add to Cart — clamp in the capture phase, before product-form.js
  //    (a bubble-phase 'submit' listener) reads FormData from the form.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (form && form.querySelectorAll) form.querySelectorAll(SELECTOR).forEach(clamp);
    },
    true
  );

  // 3. Buy Now / dynamic checkout — Shopify's payment button does not fire the
  //    form 'submit' event, so clamp the moment it is pressed (before its own
  //    handler runs). pointerdown fires earliest; mousedown is the fallback.
  const BUY_NOW_SELECTOR =
    '.shopify-payment-button, [data-shopify="payment-button"], shopify-payment-terms';

  function onBuyNowPress(e) {
    const t = e.target;
    if (t && t.closest && t.closest(BUY_NOW_SELECTOR)) clampAll();
  }

  document.addEventListener('pointerdown', onBuyNowPress, true);
  document.addEventListener('mousedown', onBuyNowPress, true);
})();
